/**
 * Constantes d'équilibrage et de réglage.
 *
 * Règle projet (§19) : **toute** valeur réglable vit ici, jamais en dur dans la logique.
 * L'économie doit pouvoir être retouchée sans ouvrir un seul fichier de `src/core`.
 *
 * Les constantes d'économie — coûts des bâtiments, production, paliers de terrain,
 * multiplicateur de prestige, jalons de gemmes, durée et intensité des boosts — arrivent
 * à l'étape 4 (Phase 1). Ce fichier commence avec le réglage de la caméra.
 */

export const CAMERA_BALANCE = {
  /**
   * Largeur d'une tuile à l'écran, en pixels CSS, au démarrage et au recentrage.
   *
   * C'est un réglage de ressenti, pas de géométrie : la tuile source fait 512 px de large
   * (voir `render/tileGeometry`), le zoom initial en découle par simple division. Monter
   * cette valeur rapproche la caméra et rend la texture des tuiles plus lisible ; la
   * baisser fait entrer davantage de ville à l'écran.
   *
   * Repère : à 100 px par tuile, un téléphone de 390 px de large montre environ 4 tuiles
   * de front, un écran de 1900 px en montre une vingtaine.
   */
  initialTileScreenWidth: 100,

  /**
   * Tuile la plus petite atteignable en dézoomant. Butée **absolue**, indépendante de la
   * taille de la grille : sans elle, un plancher relatif à l'ajustement écran devient plus
   * grand que le zoom voulu sur un large moniteur, et interdit le cadrage de départ.
   */
  minTileScreenWidth: 24,

  /**
   * Largeur de tuile à l'écran à partir de laquelle les **pastilles de palier**
   * apparaissent (ROADMAP 3.4).
   *
   * Exprimée dans la MÊME unité que les autres bornes de caméra — une largeur de tuile,
   * pas un facteur de zoom — pour qu'elle se lise et se règle sans conversion. En
   * dessous, la ville se regarde d'ensemble et une pastille par bâtiment la
   * transformerait en tableau de bord ; au-dessus, on inspecte un quartier, et savoir
   * quel bâtiment est déjà au palier 3 devient l'information utile.
   *
   * Le seuil est franchi progressivement : la pastille se fond entre cette valeur et le
   * zoom de départ, elle n'apparaît pas d'un coup.
   */
  pipTileScreenWidth: 68,

  /** Tuile la plus grande atteignable en zoomant : au-delà, la source de 512 px devient floue. */
  maxTileScreenWidth: 320,
} as const;

/* ── Économie (§6) ────────────────────────────────────────────────────────
   Toute la progression se règle ici. Aucune de ces valeurs ne doit apparaître
   dans `core/economy.ts` : la logique lit, elle ne décide pas.

   Cibles de progression imposées par le §6, à vérifier après chaque retouche :
     · premier bâtiment    ~10 s
     · deuxième type       ~2 min
     · premier terrain     ~15 min
     · premier prestige    2 à 4 h de jeu actif
   `tests/economy.test.ts` mesure ces quatre jalons et échoue s'ils dérivent. */

export const ECONOMY = {
  /** Pièces au tout premier lancement : de quoi acheter la première maison sans attendre. */
  startingCoins: 15,

  /**
   * Croissance du coût à chaque exemplaire d'un même bâtiment : `coût(n) = base × 1.15^n`.
   * Valeur canonique du genre — plus bas, la progression s'écrase ; plus haut, elle mure.
   */
  /**
   * Croissance du coût par exemplaire.
   *
   * Relevée de 1,15 à 1,18 : à 1,15 la production dépassait les coûts trop vite, et
   * chaque palier tombait sans qu'on ait eu le temps de le viser — or c'est l'attente
   * d'un objectif, pas son obtention, qui tient un joueur. 1,18 reste sous la valeur
   * classique du genre (1,15 à 1,25) tout en rendant chaque achat significatif.
   */
  costGrowth: 1.18,

  /** Tick logique, découplé du rendu (§6). */
  tickHz: 10,

  /** Gains hors-ligne plafonnés : `min(temps écoulé, 8 h)`. */
  offlineCapMs: 8 * 60 * 60 * 1000,

  /**
   * Part des gains conservée hors-ligne. À 1, laisser le jeu fermé vaut autant que
   * jouer, et la boucle de retour perd tout intérêt.
   */
  /**
   * Rendement hors-ligne, en fraction de la production normale.
   *
   * Abaissé de 0,5 à 0,25 : à la moitié du rendement, laisser tourner une nuit valait
   * mieux que jouer, ce qui inverse l'incitation d'un jeu dont l'intérêt est de composer
   * sa ville. Un quart récompense l'absence sans la rendre préférable à la présence.
   */
  offlineEfficiency: 0.25,

  /** Extension de terrain : bandes achetées en pièces, coût fortement croissant (§6). */
  /**
   * Revente d'un bâtiment, en fraction du prix payé.
   *
   * 50 % : assez pour que défaire une erreur ne soit pas puni, assez peu pour que
   * revendre-racheter ne devienne pas une stratégie. Le déplacement, lui, est GRATUIT
   * (§7) — c'est la composition de la ville qui crée l'attachement, donc le partage.
   */
  sellRefund: 0.5,
  /**
   * Amélioration d'un bâtiment, en multiple du **prix courant du type**.
   *
   * ── L'exploit que ce changement supprime ──────────────────────────────────
   *
   * La formule valait `prix de BASE × 12 × palier²` : elle ne dépendait donc ni du
   * nombre d'exemplaires possédés, ni du nombre d'améliorations déjà achetées. Mesuré à
   * l'audit du 17 août : améliorer 96 maisons de t1 à t3 coûtait **86 400 pièces** et
   * sextuplait leur production, pendant que la 97ᵉ maison coûtait **2,4 × 10⁸** pour
   * +0,5/s. Un rapport de l'ordre de **10⁶** en faveur de l'amélioration — le levier le
   * plus rentable du jeu était aussi le moins cher, et il le devenait de plus en plus.
   *
   * ── Ce que la nouvelle formule vise ───────────────────────────────────────
   *
   * `prix du prochain exemplaire × facteur × palier`. L'amélioration suit donc la même
   * courbe que l'achat et reste comparable à lui à tout moment de la partie. À 1,2, elle
   * rend environ 25 % de production de plus par pièce dépensée que l'achat : assez pour
   * qu'on la choisisse parfois, jamais assez pour qu'on ne fasse plus que ça.
   *
   * Son vrai rôle apparaît quand le terrain est plein : c'est alors la seule façon de
   * continuer à croître — et c'est ce qui la rend intéressante plutôt qu'obligatoire.
   */
  upgradeCostFactor: 1.2,
  /** Production multipliée par palier : ×1, ×2,5, ×6. */
  tierMultipliers: [1, 2.5, 6],

  land: {
    /** Chaque achat ajoute cette bande sur les deux axes. */
    stepPerPurchase: 2,
    baseCost: 5_000,
    costGrowth: 3.2,
    /** Au-delà, il faut prestiger : une grille sans fin tue la lisibilité de la ville. */
    maxPurchases: 12,
  },
} as const;

/**
 * Prestige (§6, étape 7) — la boucle qui empêche le jeu de se terminer.
 *
 * Les trois valeurs se lisent ensemble. `scale` fixe l'échelle d'une étoile, `minStars`
 * dit à partir de quand l'offre devient bonne, `perStar` dit ce qu'elle rapporte.
 *
 * Calibrage, à revérifier après toute retouche de l'économie : une première partie
 * produit de l'ordre de **10⁹ pièces en deux à quatre heures** — la cible du §6. À 10⁹,
 * `cbrt(10⁹ / 10⁶)` donne **dix étoiles**, soit **×2**. Le seuil de cinq étoiles place
 * donc la première offre de prestige un peu avant, quand elle vaut déjà ×1,5.
 */
/**
 * ══ DÉFRICHAGE — retirer une décoration d'ambiance (ROADMAP 5.3) ═══════════
 *
 * « On ne peut toujours pas supprimer les décorations mises de base ? Il faut payer pour
 * les supprimer comme Clash of Clans ? » (Benoit, 21 août). Réponse tranchée le même
 * jour : **payant, en pièces, à prix croissant.**
 *
 * ⚠️ En PIÈCES, jamais en gemmes, et c'est la décision qui compte. Un arbre qui gêne la
 * composition d'une ville est un problème d'ESTHÉTIQUE, pas de puissance : le faire payer
 * en monnaie premium reviendrait à vendre le droit d'avoir une belle ville, ce que la
 * Loi 1 de `GEMMES.md` interdit — un joueur qui ne paie jamais doit obtenir la même chose,
 * plus lentement.
 *
 * Le prix CROÎT parce qu'un défrichage gratuit et illimité rendrait la nature décorative
 * sans intérêt : on raserait tout en dix secondes et la ville perdrait ce qui la rendait
 * vivante. Croissant, chaque retrait est un choix — on dégage ce qui gêne vraiment.
 *
 * `growth` est plus doux que celui des bâtiments (1,15) : à 1,35, dix défrichages coûtent
 * déjà 28 fois le premier, ce qui borne naturellement le rasage total sans jamais
 * l'interdire. Le §1 tient : ce n'est pas un plafond, c'est une pente.
 */
export const CLEARING = {
  /** Prix du premier défrichage. Volontairement bas : le geste doit être essayé. */
  base: 250,
  /** Facteur par défrichage déjà payé, sur l'ère en cours. */
  growth: 1.35,
} as const;

export const PRESTIGE = {
  /*
    Pièces d'une ère par étoile, au cube : `étoiles = ∛(produit / scale)`.

    **Cette constante est CALIBRÉE sur une partie simulée, pas choisie.** Sa première
    valeur — 10⁶ — venait d'un raisonnement sur les prix, et rendait le prestige
    accessible en **31 minutes** là où le §6 vise deux à quatre heures. Le défaut ne
    pouvait pas se voir sur un coût : il ne se voit que sur la courbe de production
    réelle, laquelle dépend de ce que le joueur a pu POSER, donc des routes et du
    terrain. C'est la règle 21 en une ligne.

    La courbe mesurée (`tests/balance.test.ts`, 8 h de jeu actif) :

    | temps | pièces produites | étoiles avec scale = 10¹¹ |
    | ----- | ---------------- | ------------------------- |
    | 1 h   | 1,3 · 10¹⁰       | 0                         |
    | 2 h   | 7,7 · 10¹²       | 4                         |
    | 2,4 h | ~1,4 · 10¹³      | **5 → prestige ouvert**   |
    | 4 h   | 6,1 · 10¹³       | 8                         |
    | 8 h   | 2,0 · 10¹⁴       | 12                        |

    La racine cubique n'est pas décorative : elle fait que **s'acharner sur une ère
    rapporte de moins en moins**. Doubler le temps de jeu entre 4 h et 8 h n'ajoute que
    quatre étoiles. C'est ce qui rend le reset désirable au lieu d'être subi.

    ⚠️ Toute retouche de `ECONOMY` déplace cette courbe. On relance
    `npx vitest run tests/balance.test.ts --reporter=verbose`, on lit la ligne de
    progression, et on recalibre — on ne devine pas.
  */
  scale: 100_000_000_000,
  /** Multiplicateur permanent ajouté par étoile. */
  perStar: 0.1,
  /**
   * Étoiles minimales pour que le prestige soit proposé.
   *
   * À une seule étoile, le gain (+10 %) ne compenserait jamais la perte de la ville : le
   * bouton serait actif alors qu'il ne faut pas le presser. Un seuil qui rend l'offre
   * toujours BONNE vaut mieux qu'un seuil qui la rend toujours possible.
   */
  minStars: 5,
} as const;

/**
 * Ce que les gemmes achètent (§6, §14).
 *
 * ⚠️ **Jamais d'avantage de production permanent.** Le §14 est absolu : pas de
 * pay-to-win sur la production. Une accélération PONCTUELLE est autorisée — le temps de
 * jeu la rattrape —, un multiplicateur définitif ne l'est pas.
 *
 * Avant ce bloc, les gemmes n'avaient **aucune dépense dans tout le dépôt** : trois
 * quêtes par jour, trois par semaine, une chaîne de connexion et soixante-dix jalons
 * récompensaient une monnaie morte. Une récompense qui ne s'échange contre rien n'est
 * pas une récompense, c'est un compteur.
 */
export const GEM_SPENDING = {
  /**
   * Accélérations ponctuelles — une ÉCHELLE, pas une valeur unique (G§3, A2).
   *
   * Une offre unique à 20 gemmes plafonnait la dépense d'un joueur régulier bien en
   * dessous de son revenu : mesuré à ~29 gemmes par jour pour deux dépenses possibles,
   * dont l'une bornée à douze achats par ère. Trois paliers donnent au joueur du soir une
   * offre à sa taille, et au gros solde une raison d'exister.
   *
   * Le prix monte plus vite que le produit `multiplicateur × durée` : le grand palier
   * coûte dix fois le petit pour huit fois sa durée et deux fois et demie son
   * multiplicateur. C'est volontaire — un tarif dégressif sur la puissance ferait du plus
   * gros achat le seul rationnel, et il n'y aurait plus de choix, juste un prix.
   */
  rush: [
    { id: 'breeze', cost: 20, multiplier: 2, durationMs: 30 * 60 * 1000 },
    { id: 'gust', cost: 60, multiplier: 3, durationMs: 2 * 60 * 60 * 1000 },
    { id: 'gale', cost: 200, multiplier: 5, durationMs: 8 * 60 * 60 * 1000 },
  ],

  /**
   * Avance de production — le « coup de pouce » (G§3, A1).
   *
   * Crédite immédiatement N secondes de production. Trois propriétés la rendent
   * compatible avec le §14, et aucune n'est facultative :
   *
   * 1. **le montant est proportionnel à la production du moment**, donc il ne fait jamais
   *    franchir un palier que le joueur n'aurait pas atteint en attendant ;
   * 2. **il se calcule HORS accélérations** (voir `buySkip`) : sans cette règle, une
   *    avance de douze heures achetée pendant un ×5 rendrait soixante heures, et
   *    l'enchaînement deviendrait une pompe ;
   * 3. **il ne crédite PAS `totalProduced`** — donc ni jalon, ni étoile de prestige, ni
   *    rang de ligue. C'est la frontière exacte du §14 : les gemmes achètent des pièces,
   *    jamais de la progression.
   */
  skip: {
    tiers: [
      { id: 'hour', cost: 15, seconds: 60 * 60 },
      { id: 'evening', cost: 40, seconds: 4 * 60 * 60 },
      { id: 'day', cost: 90, seconds: 12 * 60 * 60 },
    ],
    /** Achats par jour. Sans ce plafond, l'avance devient la production principale. */
    dailyCap: 3,
  },

  /**
   * Extension de terrain payée en gemmes.
   *
   * Prix FIXE, là où le prix en pièces croît d'un facteur 3,2 à chaque extension : c'est
   * ce qui en fait un raccourci désirable en fin d'ère sans jamais remplacer l'économie
   * de pièces au début. Le §14 tient toujours — le terrain n'est pas de la production,
   * il est de la place, et le temps de jeu l'obtient aussi.
   */
  land: {
    cost: 45,
  },

  /**
   * Nuit blanche — le rachat des heures écrêtées (G§3, A4).
   *
   * Les gains hors-ligne sont plafonnés à 8 h et rendus à 25 % (`offlineCapMs`,
   * `offlineEfficiency`). La nuit blanche desserre les deux **pour un seul retour**, puis
   * se consomme.
   *
   * C'est l'offre la mieux placée du jeu, et la plus honnête : l'écran de retour affiche
   * déjà les heures que le plafond a écartées (`cappedMs`), donc le joueur paie pour du
   * temps qu'il a réellement accumulé et qu'il peut vérifier lui-même. Elle ne crée
   * aucune production nouvelle — elle en récupère une part déjà produite.
   */
  night: {
    cost: 25,
    capMs: 24 * 60 * 60 * 1000,
    efficiency: 0.5,
  },

  /** Relance des trois quêtes du jour (G§3, A6). Une par jour. */
  reroll: {
    cost: 10,
  },

  /**
   * Rachat de la chaîne de connexion rompue (G§6, F1).
   *
   * ⚠️ **Le plafond hebdomadaire n'est pas un réglage, c'est la condition d'honnêteté de
   * l'offre.** Sans lui, la chaîne cesse d'être un engagement et devient un abonnement :
   * on ne revient plus, on paie. Et comme la récompense de chaîne est elle-même en gemmes
   * (`streakReward`, 3 à 9), un rachat illimité à 30 resterait perdant tout en faisant du
   * jeu un distributeur — ce qui n'est pas mieux.
   */
  streakRepair: {
    cost: 30,
    /** Rachats autorisés par semaine. */
    weeklyCap: 1,
  },

  /**
   * Revente d'une décoration ACHETÉE, en pièces (ROADMAP 2.9d).
   *
   * ══ Pourquoi la vente était refusée, et ce qui change ════════════════════════
   *
   * La 0.62.0 refusait de vendre un objet payé en gemmes, et l'argument tenait :
   * `traceSellValue` paie en SECONDES DE PRODUCTION COURANTE. Vendre une fontaine à
   * 40 gemmes en fin d'ère aurait rendu une fortune — une conversion gemmes → pièces
   * dont le taux monte avec la partie, c'est-à-dire une pompe.
   *
   * Le barème ci-dessous ferme la brèche au lieu de retirer le refus : le montant est
   * indexé sur le **prix d'achat en gemmes**, jamais sur la production. Il est donc
   * FIXE — appréciable la première heure, négligeable ensuite. Le taux ne peut pas
   * monter, donc l'aller-retour ne peut pas devenir rentable.
   *
   * ⚠️ **C'est un remboursement symbolique, et c'est voulu.** Le vrai « défaire » est
   * le sac à dos (2.9c) : gratuit, sans perte, et il rend l'objet. Vendre, c'est s'en
   * séparer pour de bon — l'offre existe pour que le geste ne soit pas REFUSÉ, pas
   * pour qu'il rapporte. Un barème généreux ferait de la décoration un placement, et
   * c'est exactement ce qu'une décoration ne doit pas être.
   *
   * `refund` n'est pas une constante de plus : c'est `ECONOMY.sellRefund`, la moitié
   * déjà retenue pour les bâtiments (« assez pour que défaire une erreur ne soit pas
   * puni, assez peu pour que revendre-racheter ne devienne pas une stratégie »).
   * Dupliquer la valeur ici la ferait diverger à la première retouche (règle 22).
   */
  decorationResale: {
    /**
     * Pièces rendues par gemme dépensée, avant l'abattement.
     *
     * Dix, soit deux tiers de la mise de départ (`ECONOMY.startingCoins`) pour la
     * décoration la moins chère : de quoi reposer une première maison, jamais de quoi
     * financer un quartier.
     */
    coinsPerGem: 10,
  },
} as const;

/**
 * Accélérations simultanées (G§3, A3).
 *
 * ── Le défaut que ce bloc supprime ───────────────────────────────────────────
 *
 * Pub récompensée, boost de visite et accélération gemmes écrivaient tous les trois le
 * MÊME emplacement (`rushMultiplier`/`rushUntil`). Trois conséquences, toutes mesurées :
 * l'offre payante était grisée pendant une pub **gratuite** — le seul moment où le joueur
 * veut payer était celui où on lui disait non ; un boost de visite mérité pouvait être
 * écrasé par un achat ; et une pub regardée pendant un ×5 le remplaçait par un ×2.
 *
 * Les boosts sont donc une LISTE, et le multiplicateur est leur produit. C'est ce que
 * `gameState` annonçait déjà : « le jour où deux boosts se cumulent, c'est ici qu'on
 * ouvrira — et la migration sera triviale, les deux champs se dérivant d'une liste. »
 */
export const BOOSTS = {
  /**
   * Produit maximal des accélérations cumulées.
   *
   * Une borne est obligatoire : sans elle, trois sources qui se multiplient donnent ×30 et
   * l'économie se décale d'un ordre de grandeur pendant huit heures. Le §9 la prévoyait
   * déjà pour les visites (« plafond de 10 boosts de visite actifs ») ; elle vaut pour
   * toutes les sources, et elle s'applique **au produit**, pas au compte.
   */
  maxProduct: 20,
  /**
   * Boosts retenus dans le snapshot, expirés compris.
   *
   * Les entrées expirées sont purgées à chaque lecture, mais une borne dure protège la
   * taille de la sauvegarde (256 Ko, contrainte de base) contre un chemin de code qui
   * n'appellerait jamais la purge.
   */
  maxEntries: 12,
} as const;

/** Gemmes accordées par les jalons (§6). Jamais produites passivement. */
export const MILESTONE_REWARDS = {
  /** Premier exemplaire d'un type de bâtiment. */
  firstOfType: 1,
  /** Chaque extension de terrain. */
  landExpansion: 2,
  /** Paliers de visiteurs reçus (§9). */
  /**
   * Paliers de visiteurs. **Générés, pas énumérés** (principe premier, §1).
   *
   * Une liste écrite à la main se termine, et le jeu affiche alors une barre de
   * progression pleine qui ne bougera plus jamais — exactement l'état « j'ai fini »
   * que le jeu ne doit pas avoir. La suite double à chaque palier sur 40 crans :
   * un joueur ne l'épuisera pas.
   */
  visitorTiers: Array.from({ length: 40 }, (_, index) => 10 * 2 ** index),
  visitorTierReward: 3,
  /**
   * Paliers de production totale, en pièces cumulées. **Générés, pas énumérés.**
   *
   * ×10 à chaque cran sur 60 crans, soit jusqu'à 1e63 — hors d'atteinte même après des
   * dizaines de prestiges, et le formateur de `core/format.ts` va bien plus loin encore.
   * L'ancienne liste s'arrêtait à 1 Md : un joueur l'atteignait en une soirée, et se
   * retrouvait sans objectif affiché.
   */
  productionTiers: Array.from({ length: 60 }, (_, index) => 1_000 * 10 ** index),
  productionTierReward: 2,
  /**
   * Paliers de RÉSEAU ROUTIER, en cases de rue. **Générés, pas énumérés** (ROADMAP 3.2).
   *
   * Le réseau se trace tout seul à chaque pose : ces paliers récompensent donc la ville
   * qui s'étend, pas une action séparée — c'est le même geste, vu sous un autre angle.
   * Progression géométrique douce : 10, 20, 40… jusqu'à 1 280, au-delà de ce qu'une
   * grille de 22 × 22 peut porter.
   */
  roadTiers: Array.from({ length: 8 }, (_, index) => 10 * 2 ** index),
  roadTierReward: 2,

  /**
   * Paliers de DÉCORATION posée — traces de visiteurs et objets payés confondus.
   *
   * Ils récompensent ce que le joueur MONTRE : c'est la famille C du `GEMMES.md`, et
   * c'est aussi ce qu'un visiteur voit en arrivant sur la page publique.
   */
  decorTiers: [1, 3, 6, 12, 25],
  decorTierReward: 3,

  /** Premier prestige. */
  firstPrestige: 5,

  /**
   * Paliers d'ÈRES, au-delà de la première.
   *
   * ⚠️ La première ère garde son jalon à part (`firstPrestige`, 5 gemmes) : c'est le
   * franchissement le plus difficile de la partie, et le confondre avec les suivants
   * effacerait ce qu'il représente.
   */
  eraTiers: [2, 3, 5, 8, 13, 21],
  eraTierReward: 6,
} as const;

/**
 * Publicité récompensée (demande de Benoit, 18 août 2026).
 *
 * Le §14 tient : regarder une pub n'est pas payer — c'est du temps, comme jouer.
 * La récompense passe par le MÊME emplacement que l'accélération gemmes
 * (`rushMultiplier`/`rushUntil`) : une seule accélération à la fois, règle déjà
 * établie. Le plafond quotidien borne l'ensemble — sans lui, la pub deviendrait
 * la source de production principale et le jeu un distributeur de spots.
 */
export const REWARDED_AD = {
  multiplier: 2,
  durationMs: 60 * 60 * 1000,
  /** Visionnages récompensés par jour. */
  dailyCap: 3,
} as const;

/** Décorations laissées par les visiteurs (§16.1). Paliers, pas une par visiteur. */
export const VISITOR_DECORATIONS = {
  /** Une fleur par visiteur au début. */
  flowerPerVisitor: 1,
  tiers: [
    { at: 25, decoration: 'bench' },
    { at: 100, decoration: 'fountain' },
    { at: 500, decoration: 'arch' },
    { at: 1000, decoration: 'goldenStatue' },
  ],
  /**
   * Prix de VENTE d'une trace, en secondes de production courante (0.36.0, demande
   * de Benoit : la vente comme motivation de partage). En secondes et non en pièces :
   * un prix fixe deviendrait de la poussière dès la deuxième heure. Le §14 tient —
   * ces pièces récompensent des visites réelles validées par le serveur.
   */
  sellSeconds: {
    flowerbed: 90,
    planter: 90,
    bench: 900,
    fountain: 5400,
  } as Record<string, number>,
} as const;
